import { useEffect, useState } from 'react';

/** Console routes live in the URL hash (#/lugares), so the static host needs no rewrites. */
export type Route =
  | { readonly name: 'home' }
  | { readonly name: 'places' }
  | { readonly name: 'place'; readonly id: string }
  | { readonly name: 'menu'; readonly placeId: string; readonly menuId: string }
  | { readonly name: 'pilot' }
  | { readonly name: 'account' }
  | { readonly name: 'not_found' };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, '').replace(/\/+$/, '') || '/';
  if (path === '/') return { name: 'home' };
  if (path === '/lugares') return { name: 'places' };
  if (path === '/cuenta') return { name: 'account' };
  if (path === '/lista-piloto') return { name: 'pilot' };
  const place = /^\/lugares\/([^/]+)$/.exec(path);
  if (place?.[1] && UUID.test(place[1])) return { name: 'place', id: place[1] };
  const menu = /^\/lugares\/([^/]+)\/cartas\/([^/]+)$/.exec(path);
  if (menu?.[1] && menu[2] && UUID.test(menu[1]) && UUID.test(menu[2])) {
    return { name: 'menu', placeId: menu[1], menuId: menu[2] };
  }
  return { name: 'not_found' };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'places':
      return '#/lugares';
    case 'place':
      return `#/lugares/${route.id}`;
    case 'menu':
      return `#/lugares/${route.placeId}/cartas/${route.menuId}`;
    case 'pilot':
      return '#/lista-piloto';
    case 'account':
      return '#/cuenta';
    case 'not_found':
      return '#/';
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
